import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import EmailCaptureForm from "web/react/components/email-capture-form/email-capture-form";
import LazyModal from "web/react/components/lazy-modal/lazy-modal";
import { Heading } from "web/react/emo/heading";
import { VStack } from "web/react/emo/v-stack";
import withReduxProvider from "web/react/redux-provider";
import {
    ANDROID_SIGNUP_CAPTURE_TYPES,
    closeCustomerCaptureOverlay,
} from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import { SocialLogin } from "web/script/utils/social-auth-login";
import url from "web/script/utils/url";
import styles from "./email-capture-overlay.module.css";

function mapStateToProps(state) {
    const { captureType, designerId, next, open, productId, searchGender, searchTerm } =
        state.customerCaptureOverlay;
    return {
        captureType,
        designerId,
        next,
        overlayOpen: open,
        productId,
        searchGender,
        searchTerm,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        closeCustomerCaptureOverlay: () => dispatch(closeCustomerCaptureOverlay()),
    };
}

export async function socialLogin({
    authProvider,
    next,
    marketingEmails,
    productId,
    designerId,
    type,
    searchTerm,
    searchGender,
}) {
    // start with the current URL (including all query parameters)
    const parsedURL = url.parse(window.location.href);

    // work out what the 'next' parameter should be
    const nextParam = next || url.unparse(parsedURL);

    // update the path to point to the correct auth provider
    parsedURL.searchParams.set("next", nextParam);

    parsedURL.searchParams.set("isLogin", false.toString());
    parsedURL.searchParams.set("marketingEmails", marketingEmails);

    parsedURL.searchParams.set("productId", productId);
    parsedURL.searchParams.set("designerId", designerId);
    parsedURL.searchParams.set("authType", type);
    parsedURL.searchParams.set("searchQuery", searchTerm);
    parsedURL.searchParams.set("searchGender", searchGender);

    // do the redirect
    try {
        const redirectURL = await SocialLogin(parsedURL, authProvider);
        window.location.href = redirectURL;
        return true;
    } catch (e) {
        return false;
    }
}

export function EmailCaptureOverlay({
    captureType: captureTypeUntyped,
    closeCustomerCaptureOverlay,
    cookiePolicyLink,
    designerId,
    next,
    overlayOpen,
    privacyPolicyLink,
    productId,
    searchGender,
    searchTerm,
    termsAndConditionsLink,
    shortPolicy,
    signInLink,
    contentOnly,
    title,
    showSocial = false,
    autoFocus = true,
    onCtaClick,
}) {
    /**
     * @type {import('web/redux/ducks/customer-capture-overlay').CaptureType}
     */
    const captureType = captureTypeUntyped;
    let appElement;
    if (document.body) {
        appElement = document.body.querySelector(".site-wrapper") || undefined;
    }

    const emailLabelText = gettext("account.form.email_label");

    let submitText;
    let type;

    switch (captureType) {
        case "signup_checkout_save":
        case "signup_pdp_save":
        case "signup_feed_save":
            submitText = gettext("account.register.return_lead_btn_txt_track_deals");
            title = gettext("saved_items.sign_up_save_item_title_option_b");
            type = "save_for_later";
            break;
        case "signup_lead_save":
            submitText = gettext("account.register.return_lead_btn_txt_get_deals");
            title = gettext("account.register.return_lead_title_join_lyst");
            type = "save_for_later_lead_return";
            break;
        case "signup_search":
            submitText = gettext("account.register.return_lead_btn_follow_search");
            title = gettext("account.register.return_lead_title_follow_search", {
                search_terms: searchTerm,
            });
            type = "saved_search";
            break;
        case "signup_oos_save":
        case "stock_alert":
            submitText = gettext("general.alert_me");
            title = gettext("email_capture.stock_alert.title");
            type = "back_in_stock";
            break;
        case "stock_alert_lead_return":
            submitText = gettext("general.alert_me");
            title = gettext("email_capture.stock_alert.title_when_triggered_by_lead_link");
            type = "back_in_stock";
            break;
        case "size_stock_alert":
            // "Alert me"
            submitText = gettext("general.alert_me");
            // We’ll let you know when this is back in stock in your size"
            title = gettext("member_signup.member_overlay.oos_size.title");
            type = "save_for_later";
            break;
        case "signup_du":
        case "signup_du_banner":
            submitText = gettext("account.register.return_lead_btn_follow_products_designer");
            // "Follow new products and deals from this designer"
            title = gettext("account.register.return_lead_title_follow_products_designer");
            type = "designer_update_lyst";
            break;
        case "signup_su":
            // "Follow store"
            submitText = gettext("account.register.return_lead_btn_follow_products_store");
            // "Follow new products and deals from this store"
            title = gettext("account.register.return_lead_title_follow_products_store");
            type = "store_update_lyst";
            break;
        case "feeds_show_more":
            submitText = gettext("member_signup.member_overlay.cta");
            type = "feeds_show_more";
            break;
        case "signup_banner":
            submitText = gettext("member_signup.member_overlay.cta");
            type = "signup_banner";
            break;
        case "related_products":
            submitText = gettext("member_signup.member_overlay.cta");
            type = "related_product";
            break;
        case "feeds_product_card":
            submitText = gettext("member_signup.member_overlay.cta");
            type = "feeds_product_card";
            break;
    }

    // Hooks
    const [emailAddress, setEmailAddress] = useState("");
    const [privacyPolicy, setPrivacyPolicy] = useState(false);
    const [marketingEmails, setMarketingEmails] = useState(false);
    /** @type {[any, any]} */
    const [formErrors, setFormErrors] = useState(undefined);

    let hidden;
    let visibilityChange;
    if (typeof document.hidden !== "undefined") {
        hidden = "hidden";
        visibilityChange = "visibilitychange";
    }

    function handleVisibilityChange() {
        if (!document[hidden]) {
            analytics.event(captureType, "view_form", "back_overlay");
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        }
    }

    useEffect(() => {
        return () => {
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        };
    });

    function triggerAfterOpen() {
        analytics.event(captureType, "open_signup_form", "open_overlay", true); // non interactive
        document.addEventListener(visibilityChange, handleVisibilityChange, false);
    }

    function triggerClose() {
        analytics.event(captureType, "close_signup_form", "exit_signup");
        document.removeEventListener(visibilityChange, handleVisibilityChange);
        closeCustomerCaptureOverlay();
    }

    function changeEmail(event) {
        setEmailAddress(event.target.value);
    }

    function handleLegalChange(privacyPolicyChecked, marketingEmailsChecked) {
        setPrivacyPolicy(privacyPolicyChecked);
        setMarketingEmails(marketingEmailsChecked);
    }

    async function onSubmit(event) {
        event.preventDefault();
        analytics.event(captureType, "click_signup_button", "signup_button");
        analytics.event(captureType, "submit_signup", "successful_signup");

        onCtaClick?.();

        const seenProducts = userProfiler.getSeenProducts();
        let productIds = [];
        if (seenProducts) {
            productIds = seenProducts.reduce((memo, product) => {
                memo.push(product.productId);
                return memo;
            }, []);
        }

        const body = {
            designer_id: designerId,
            email: emailAddress,
            invisible_pids: productIds.join(","),
            marketing_emails: marketingEmails,
            privacy_policy: privacyPolicy,
        };

        const parsedURL = url.parse(window.location.href);
        // work out what the 'next' parameter should be
        parsedURL.searchParams.set("loggedin", "true");
        // avoid 'instant-notification' showing for these capture types
        if (ANDROID_SIGNUP_CAPTURE_TYPES.includes(captureType)) {
            parsedURL.searchParams.set("area", "heart");
        }
        const path = next || parsedURL.path;
        const params = url.toQueryString(parsedURL.searchParams);
        const nextParam = next || `${path}${params && `?${params}`}`;
        const searchTermParam = searchTerm || parsedURL.searchParams.get("q");
        const searchGenderParam =
            searchGender ||
            parsedURL.searchParams.get("gender") ||
            parsedURL.searchParams.get("meta_gender");

        let jsonData = undefined;
        const queryParams = url
            .toQueryString({
                type: type,
                product_id: productId,
                designer_id: designerId,
                search_query: searchTermParam,
                search_gender: searchGenderParam,
                email_capture: "true",
                next: nextParam,
            })
            .toString();
        try {
            jsonData = await requester.post(`/account/register/?${queryParams}`, body);
        } catch (e) {
            console.error(e);
            setFormErrors({ other: [gettext("account.login.with_magic_link.error_title")] });
            analytics.event(captureType, "error_signup", "server_error");
            return;
        }

        if (jsonData && jsonData.success) {
            onCtaClick?.();
            closeCustomerCaptureOverlay();
            window.location = jsonData.data ? jsonData.data.next : nextParam;
        } else {
            const { errors } = jsonData.data;
            Object.keys(errors).map((key) => {
                if (key === "marketing_emails") {
                    analytics.event(captureType, "missed_tickbox", "tickbox");
                }
            });
            setFormErrors(errors);
        }
    }

    async function onSocialClick(authProvider) {
        // signup_facebook, signup_google
        analytics.event(captureType, "click_social_sign_up", `signup_${authProvider}`);

        if (privacyPolicy !== true || marketingEmails !== true) {
            setFormErrors({
                privacy_policy: [gettext("account.register.user_must_tick_both_boxes")],
            });
            return;
        }

        const isSocialLoginSuccessful = await socialLogin({
            authProvider,
            next,
            marketingEmails,
            productId,
            designerId,
            type,
            searchTerm,
            searchGender,
        });

        if (!isSocialLoginSuccessful) {
            setFormErrors({ other: [`Failed to log in with ${authProvider}`] });
        }
    }

    if (!overlayOpen) {
        return null;
    }

    function getEmailCaptureForm() {
        return (
            <EmailCaptureForm
                captureType={captureType}
                cookiePolicyLink={cookiePolicyLink}
                emailAddress={emailAddress}
                emailLabelText={emailLabelText}
                formErrors={formErrors}
                onEmailChange={changeEmail}
                onLegalChange={handleLegalChange}
                onSubmit={onSubmit}
                privacyPolicyLink={privacyPolicyLink}
                submitText={submitText}
                termsAndConditionsLink={termsAndConditionsLink}
                shortPolicy={shortPolicy}
                onSocialClick={onSocialClick}
                signInLink={signInLink}
                next={next}
                searchTerm={searchTerm}
                searchGender={searchGender}
                type={type}
                productId={productId}
                designerId={designerId}
                autoFocus={autoFocus}
                showSocial={showSocial}
            />
        );
    }

    if (contentOnly) {
        return getEmailCaptureForm();
    }

    return (
        <div id="customer-capture-overlay">
            <LazyModal
                appElement={appElement}
                isOpen={overlayOpen}
                onAfterOpen={triggerAfterOpen}
                onRequestClose={triggerClose}
                className={styles.modal}
                overlayClassName={styles.overlay}
            >
                <CloseButton className={styles.closeButton} onClick={triggerClose} />
                <div className={styles.captureForm}>
                    <VStack spacing="lg" align="center">
                        <Heading textStyle={"title-2"} align={"center"} as="h1">
                            {title}
                        </Heading>
                        {getEmailCaptureForm()}
                    </VStack>
                </div>
            </LazyModal>
        </div>
    );
}

export default withReduxProvider(connect(mapStateToProps, mapDispatchToProps)(EmailCaptureOverlay));

EmailCaptureOverlay.propTypes = {
    captureType: PropTypes.oneOf([
        "signup_lead_save",
        "signup_pdp_save",
        "signup_feed_save",
        "signup_du",
        "signup_search",
        "stock_alert",
        "stock_alert_lead_return",
        "signup_oos_save",
        ...ANDROID_SIGNUP_CAPTURE_TYPES,
    ]),
    closeCustomerCaptureOverlay: PropTypes.func,
    cookiePolicyLink: PropTypes.string,
    designerId: PropTypes.number,
    next: PropTypes.string,
    overlayOpen: PropTypes.bool,
    privacyPolicyLink: PropTypes.string,
    productId: PropTypes.string,
    searchGender: PropTypes.string,
    searchTerm: PropTypes.string,
    termsAndConditionsLink: PropTypes.string,
    shortPolicy: PropTypes.bool,
    signInLink: PropTypes.string,
    contentOnly: PropTypes.bool,
    title: PropTypes.string,
    showSocial: PropTypes.bool,
    autoFocus: PropTypes.bool,
    onCtaClick: PropTypes.func,
};
